import { type Ref } from "vue";
import { toast } from "@/ui-plus/sonner";
import { DELIVERYTEAM_ORDER_STATUS_OBJ } from "@lib/types/deliveryteamOrderStatus";
import { storeOrdersApiClient } from "@api-client/store_orders";
import type { ResType } from "@api-client/typeUtils";

const ApiPath = storeOrdersApiClient[":storeId"].get_by_order_ids.$post;
type RecentOrdersType = ResType<typeof ApiPath>["data"];

let loadOrders: (
    storeId: string,
    newOrderIds: string[],
    callback?: () => void
) => Promise<void>;

export const setLoadOrders = (value: (
    storeId: string,
    newOrderIds: string[],
    callback?: () => void
) => Promise<void>) => {
    loadOrders = value;
};

type CachedStoreOrdersMap = Record<string, RecentOrdersType[number]>;
type CachedStoreOrdersRef = Ref<CachedStoreOrdersMap>;

export let cachedStoreOrders: CachedStoreOrdersRef;
export const setCachedStoreOrders = (value: CachedStoreOrdersRef) => {
    cachedStoreOrders = value;
};

export const loadandCheckDeliveryteamOrder = (
    storeOrderId: string,
    deliveryteamOrderId: string
) => {
    if (!cachedStoreOrders) {
        alert('cachedStoreOrders is not set');
        return;
    }
    if (!loadOrders) {
        alert('loadOrders is not set');
        return;
    }
    const cachedOrder = cachedStoreOrders.value[storeOrderId];
    if (!cachedOrder) return;
    const storeId = cachedStoreOrders.value[storeOrderId].storeId;
    if (!storeId) return;
    loadOrders(storeId, [storeOrderId], () => {
        markDraftVerificationInProgress(storeOrderId, deliveryteamOrderId);
    });
    setTimeout(() => {
        checkDraftDeliveryteamOrder(storeOrderId, deliveryteamOrderId);
    }, 2000);
}

const markDraftVerificationInProgress = (storeOrderId: string, deliveryteamOrderId: string) => {
    if (!cachedStoreOrders) {
        alert('cachedStoreOrders is not set');
        return;
    }
    const storeOrder = cachedStoreOrders.value[storeOrderId];
    if (!storeOrder) return;
    const deliveryteamOrderIndex = storeOrder.deliveryteamOrders.findIndex((order) => order.deliveryteamOrderId === deliveryteamOrderId);
    if (deliveryteamOrderIndex === -1) return;
    const deliveryteamOrder = storeOrder.deliveryteamOrders[deliveryteamOrderIndex];
    if (!deliveryteamOrder) return;
    if (deliveryteamOrder.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.DRAFT) return;
    storeOrder.deliveryteamOrders[deliveryteamOrderIndex].isDraftVerificationInProgress = true;
}

export const checkDraftDeliveryteamOrder = async (
    storeOrderId: string,
    deliveryteamOrderId: string
) => {
    if (!cachedStoreOrders) {
        alert('cachedStoreOrders is not set');
        return;
    }
    const storeOrder = cachedStoreOrders.value[storeOrderId];
    if (!storeOrder) return;
    const deliveryteamOrderIndex = storeOrder.deliveryteamOrders.findIndex((order) => order.deliveryteamOrderId === deliveryteamOrderId);
    if (deliveryteamOrderIndex === -1) return;
    const deliveryteamOrder = storeOrder.deliveryteamOrders[deliveryteamOrderIndex];
    if (!deliveryteamOrder) return;
    if (deliveryteamOrder.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.DRAFT) return;
    storeOrder.deliveryteamOrders[deliveryteamOrderIndex].isDraftVerificationInProgress = true;
    const { data, error } = await (await storeOrdersApiClient[":storeId"].verify_draft_deliveryteam_order.$post({
        param: { storeId: storeOrder.storeId },
        json: {
            deliveryteamOrderId,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error verifying draft deliveryteam order");
        return;
    }
    if (data) {
        storeOrder.deliveryteamOrders[deliveryteamOrderIndex].paymentStatus = data.paymentStatus;
        storeOrder.deliveryteamOrders[deliveryteamOrderIndex].paymentRedirectUrl = data.paymentRedirectUrl;
    }
    storeOrder.deliveryteamOrders[deliveryteamOrderIndex].isDraftVerificationInProgress = false;
}